import { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { clamp01 } from '../../core/math';
import { CURVE_PRESETS, evalCurve, normaliseCurve, type Curve } from '../../core/params/curve';

export interface CurveEditorProps {
  value: Curve;
  onChange: (curve: Curve) => void;
  xLabel?: string;
  yLabel?: string;
  height?: number;
}

const SAMPLES = 64;

/**
 * Editor for `curve` parameters.
 *
 * Drag a point to move it, double-click the field to add one, alt-click a point to
 * remove it. The endpoints stay pinned to x = 0 and x = 1 so the mapping is always
 * defined over the whole input range.
 */
export function CurveEditor({ value, onChange, xLabel, yLabel, height = 104 }: CurveEditorProps): JSX.Element {
  const ref = useRef<SVGSVGElement>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const path = useMemo(() => {
    let d = '';
    for (let i = 0; i < SAMPLES; i++) {
      const t = i / (SAMPLES - 1);
      const y = 1 - clamp01(evalCurve(value, t));
      d += `${i === 0 ? 'M' : 'L'}${(t * 100).toFixed(2)} ${(y * 100).toFixed(2)}`;
    }
    return d;
  }, [value]);

  const toCurveSpace = (e: React.PointerEvent | React.MouseEvent): { x: number; y: number } | null => {
    const svg = ref.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    return {
      x: clamp01((e.clientX - rect.left) / rect.width),
      y: clamp01(1 - (e.clientY - rect.top) / rect.height),
    };
  };

  const movePoint = (index: number, e: React.PointerEvent): void => {
    const p = toCurveSpace(e);
    if (!p) return;
    const next = value.map((pt, i) => {
      if (i !== index) return { ...pt };
      // Endpoints keep their x so the curve always spans the full input range.
      const pinned = index === 0 || index === value.length - 1;
      return { x: pinned ? pt.x : p.x, y: p.y };
    });
    onChange(normaliseCurve(next));
  };

  return (
    <div className="space-y-1">
      <svg
        ref={ref}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ height }}
        className="w-full cursor-crosshair touch-none rounded-md border border-ink-700 bg-ink-850"
        onDoubleClick={(e) => {
          const p = toCurveSpace(e);
          if (!p) return;
          onChange(normaliseCurve([...value.map((pt) => ({ ...pt })), { x: p.x, y: p.y }]));
        }}
        onPointerMove={(e) => dragIndex !== null && movePoint(dragIndex, e)}
        onPointerUp={() => setDragIndex(null)}
        onPointerLeave={() => setDragIndex(null)}
      >
        {[25, 50, 75].map((v) => (
          <g key={v}>
            <line x1={v} y1={0} x2={v} y2={100} stroke="currentColor" className="text-ink-800" strokeWidth={0.4} />
            <line x1={0} y1={v} x2={100} y2={v} stroke="currentColor" className="text-ink-800" strokeWidth={0.4} />
          </g>
        ))}
        <path d={path} fill="none" stroke="var(--color-accent-500)" strokeWidth={1.4} vectorEffect="non-scaling-stroke" />
        {value.map((pt, i) => (
          <circle
            key={i}
            cx={pt.x * 100}
            cy={(1 - pt.y) * 100}
            r={2.6}
            className={clsx('cursor-grab', dragIndex === i ? 'fill-white' : 'fill-ink-200')}
            stroke="var(--color-ink-950)"
            strokeWidth={0.8}
            onPointerDown={(e) => {
              e.stopPropagation();
              // Alt-click removes an interior point; the endpoints must remain.
              if (e.altKey && i > 0 && i < value.length - 1) {
                onChange(value.filter((_, j) => j !== i));
                return;
              }
              (e.target as SVGCircleElement).setPointerCapture?.(e.pointerId);
              setDragIndex(i);
            }}
          />
        ))}
      </svg>

      <div className="flex items-center gap-1">
        {CURVE_PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            title={preset.label}
            onClick={() => onChange(preset.curve.map((p) => ({ ...p })))}
            className="rounded bg-ink-850 px-1.5 py-0.5 text-[9px] text-ink-400 transition-colors hover:bg-ink-800 hover:text-ink-200"
          >
            {preset.label}
          </button>
        ))}
      </div>

      {(xLabel || yLabel) && (
        <div className="flex justify-between font-mono text-[9px] text-ink-600">
          <span>{xLabel}</span>
          <span>{yLabel}</span>
        </div>
      )}
    </div>
  );
}
